@if (renderedSteps().length) {
  @let stepperLabel = ariaLabel() || ('Step progress' | translate);

  <ng-template #stepGlyph let-step let-i="i">
    @switch (step.marker) {
      @case ('check') {
        <sb-icon name="check-regular" size="sm" />
      }
      @case ('warning') {
        <sb-icon name="warning-regular" size="sm" />
        <span class="sr-only">{{ 'Needs attention' | translate }}</span>
      }
      @case ('skip') {
        <sb-icon name="skip-forward-regular" size="sm" />
      }
      @case ('disabled') {
        <sb-icon name="forbid-circle-regular" size="sm" />
      }
      @case ('locked') {
        <sb-icon name="lock-regular" size="sm" />
      }
      @default {
        {{ i + 1 }}
      }
    }
  </ng-template>

  <!--
    'locked' takes the neutral chip 'skip' uses rather than the `bg-surface` one 'number' and
    'disabled' share: that surface sits at ~1.2:1 against the panel behind it, which draws no
    circle at all on the dark theme. A number can carry its row unaided, and a forbid glyph reads
    as absence anyway — but the lock is the whole state, so its chip has to be a visible shape in
    both themes.
  -->
  <ng-template #stepContent let-step let-i="i">
    <span
      class="flex size-6 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
      [class.bg-surface]="step.marker === 'number' || step.marker === 'disabled'"
      [class.text-subtle]="step.marker === 'number' || step.marker === 'disabled'"
      [class.bg-brand]="step.marker === 'current'"
      [class.text-brand-inverted]="step.marker === 'current'"
      [class.bg-brand-subtle]="step.marker === 'check'"
      [class.text-brand]="step.marker === 'check'"
      [class.bg-neutral-subtle]="step.marker === 'skip' || step.marker === 'locked'"
      [class.text-default]="step.marker === 'skip' || step.marker === 'locked'"
      [class.bg-warning-subtle]="step.marker === 'warning'"
      [class.text-warning-strong]="step.marker === 'warning'"
    >
      <ng-container *ngTemplateOutlet="stepGlyph; context: { $implicit: step, i }" />
    </span>
    <span class="flex min-w-0 flex-col">
      <span
        class="text-base"
        [class.text-default]="step.status !== 'disabled' || step.isCurrent"
        [class.text-disabled]="step.status === 'disabled' && !step.isCurrent"
        [class.font-semibold]="step.isCurrent"
      >
        {{ step.label }}
      </span>
      @if (step.subText; as subText) {
        <span class="text-sm text-subtle">{{ subText }}</span>
      }
    </span>
  </ng-template>

  <ng-template #stepWrapper let-step let-i="i" let-direction="direction">
    @if (step.clickable) {
      <button
        type="button"
        class="reset-button flex w-full cursor-pointer items-center gap-sm hover:opacity-80"
        [class.flex-col]="direction === 'col'"
        [class.flex-row]="direction === 'row'"
        [class.text-left]="direction === 'row'"
        (click)="stepClick.emit(i)"
      >
        <ng-container *ngTemplateOutlet="stepContent; context: { $implicit: step, i }" />
      </button>
    } @else if (step.status === 'disabled') {
      <span
        class="flex w-full cursor-not-allowed items-center gap-sm"
        [class.flex-col]="direction === 'col'"
        [class.flex-row]="direction === 'row'"
        [class.text-left]="direction === 'row'"
      >
        <ng-container *ngTemplateOutlet="stepContent; context: { $implicit: step, i }" />
      </span>
    } @else {
      <ng-container *ngTemplateOutlet="stepContent; context: { $implicit: step, i }" />
    }
  </ng-template>

  @if (!hideProgressBar()) {
    <progress class="sr-only" [value]="progressPercentage()" max="100" [attr.aria-label]="stepperLabel"></progress>
  }

  <ng-template #pillContent let-step let-i="i">
    <span
      class="flex h-9 items-center justify-center gap-sm rounded-full text-base font-normal whitespace-nowrap sm:w-auto sm:px-md"
      [class]="step.pillClasses"
    >
      <ng-container *ngTemplateOutlet="stepGlyph; context: { $implicit: step, i }" />
      <span class="sm:not-sr-only" [class.sr-only]="!step.isCurrent">{{ step.label }}</span>
    </span>
  </ng-template>

  @if (variant() === 'pill') {
    <ol
      class="reset-ul flex w-full flex-wrap items-center gap-y-sm"
      [attr.aria-label]="hideProgressBar() ? stepperLabel : null"
    >
      @for (step of renderedSteps(); track $index) {
        <li class="flex items-center" [attr.aria-current]="step.isCurrent ? 'step' : null">
          @if (!$first) {
            <span
              class="h-2xs w-lg"
              [class.bg-brand-subtle]="$index <= currentStep()"
              [class.bg-surface]="$index > currentStep()"
              aria-hidden="true"
            ></span>
          }
          @if (step.clickable) {
            <button
              type="button"
              class="reset-button flex cursor-pointer rounded-full hover:opacity-80"
              (click)="stepClick.emit($index)"
            >
              <ng-container *ngTemplateOutlet="pillContent; context: { $implicit: step, i: $index }" />
            </button>
          } @else {
            <span class="flex" [class.cursor-not-allowed]="step.status === 'disabled'">
              <ng-container *ngTemplateOutlet="pillContent; context: { $implicit: step, i: $index }" />
            </span>
          }
        </li>
      }
    </ol>
  } @else if (orientation() === 'horizontal') {
    <div class="relative w-full">
      <div
        class="pointer-events-none absolute top-3 h-px bg-surface"
        [style.left]="edgeInset()"
        [style.right]="edgeInset()"
        aria-hidden="true"
      >
        <div class="h-full bg-brand-subtle" [style.width.%]="progressPercentage()"></div>
      </div>

      <ol class="reset-ul flex w-full" [attr.aria-label]="hideProgressBar() ? stepperLabel : null">
        @for (step of renderedSteps(); track $index) {
          <li
            class="relative flex flex-1 flex-col items-center gap-sm text-center"
            [attr.aria-current]="step.isCurrent ? 'step' : null"
          >
            <ng-container *ngTemplateOutlet="stepWrapper; context: { $implicit: step, i: $index, direction: 'col' }" />
          </li>
        }
      </ol>
    </div>
  } @else {
    <ol class="reset-ul flex flex-col gap-md" [attr.aria-label]="hideProgressBar() ? stepperLabel : null">
      @for (step of renderedSteps(); track $index) {
        <li class="flex flex-row items-center gap-sm py-xs" [attr.aria-current]="step.isCurrent ? 'step' : null">
          <ng-container *ngTemplateOutlet="stepWrapper; context: { $implicit: step, i: $index, direction: 'row' }" />
        </li>
      }
    </ol>
  }
}
